import React, { useMemo, useState } from 'react';

import MissionControlLayersFiltersView from './MissionControlLayersFilters.view';
import JobStatus from 'utils/enums/job-status';
import { useTranslation } from 'react-i18next';
import { MSelectChangeData, MSelectOption } from 'views/MSelect/MSelect.model';
import { OnChangeValue } from 'react-select';
import { ConnectorPowerLabel, ConnectorTypeLabel } from 'utils/enums/chargingType';

type Props = {
  onDateFilterChange: (startDate: moment.Moment | null, endDate: moment.Moment | null) => void;
  onResetDate?: () => void;
  onGeneralFilterChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPowerTypeFilterChange: (value: OnChangeValue<MSelectChangeData<null, ConnectorPowerLabel>, true>) => void;
  onClearGeneralFilter: () => void;
  onConnectorTypeFilterChange: (value: OnChangeValue<MSelectChangeData<null, ConnectorTypeLabel>, true>) => void;
  powerTypeFilters: MSelectOption<null, ConnectorPowerLabel>[];
  generalFilterValue: string;
  dateFilters: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  };
  connectorTypeFilters: MSelectOption<null, ConnectorTypeLabel>[];
};

const MissionControlLayersFilters: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const [zonesFiltersState, setZonesFiltersState] = useState<readonly MSelectOption[]>([]);

  const [statusFiltersState, setStatusFiltersState] = useState<JobStatus[]>([
    JobStatus.New,
    JobStatus.Routing,
    JobStatus.Routed,
    JobStatus.Picked,
    JobStatus.Dropped,
    JobStatus.Noshow,
  ]);

  const { t } = useTranslation();

  const powerTypeOptions = useMemo(() => {
    return Object.values(ConnectorPowerLabel).map((powerType: ConnectorPowerLabel) => {
      return { label: t(`missionControl.layers.filters.powerType.options.${powerType}`), id: powerType };
    });
  }, [t]);

  const connectorTypeOptions = useMemo(() => {
    return Object.values(ConnectorTypeLabel).map((connectorType: ConnectorTypeLabel) => {
      return { label: t(`missionControl.layers.filters.connectorType.options.${connectorType}`), id: connectorType };
    });
  }, [t]);

  const statusOptions = useMemo(() => {
    return Object.values(JobStatus).map((status: JobStatus) => {
      return { label: t(`missionControl.layers.filters.status.options.${status}`), id: status };
    });
  }, [t]);

  const statusFilters = statusFiltersState.map((status: JobStatus) => {
    return { id: status, label: t(`missionControl.layers.filters.status.options.${status}`).toString() };
  });

  const updateStatusFilter = (value: OnChangeValue<MSelectChangeData<null, JobStatus>, true>) => {
    setStatusFiltersState(() => value.map((item: MSelectOption<null, JobStatus>) => item.id));
  };

  const removeStatusFilterValue = (id: JobStatus) => {
    setStatusFiltersState((prev: JobStatus[]) => {
      const valueIndex = prev.findIndex((status: JobStatus) => status === id);

      if (valueIndex === -1) {
        return prev;
      }

      const newState = [...prev];

      newState.splice(valueIndex, 1);

      return newState;
    });
  };

  const removeZonesFilterValue = (id: string) => {
    setZonesFiltersState((prev: readonly MSelectOption[]) => {
      const valueIndex = prev.findIndex((zone: MSelectOption) => zone.id === id);

      if (valueIndex === -1) {
        return prev;
      }

      const newState = [...prev];

      newState.splice(valueIndex, 1);

      return newState;
    });
  };

  return (
    <MissionControlLayersFiltersView
      connectorTypeFilters={props.connectorTypeFilters}
      onConnectorTypeFilterChange={props.onConnectorTypeFilterChange}
      onClearGeneralFilter={props.onClearGeneralFilter}
      onGeneralFilterChange={props.onGeneralFilterChange}
      generalFilterValue={props.generalFilterValue}
      zonesFilters={zonesFiltersState}
      powerTypeFilters={props.powerTypeFilters}
      statusFilters={statusFilters}
      onRemoveStatusFilter={removeStatusFilterValue}
      onRemoveZonesFilter={removeZonesFilterValue}
      onStatusFilterChange={updateStatusFilter}
      onPowerTypeFilterChange={props.onPowerTypeFilterChange}
      statusOptions={statusOptions}
      powerTypeOptions={powerTypeOptions}
      connectorTypeOptions={connectorTypeOptions}
      onDateFilterChange={props.onDateFilterChange}
      onZonesFilterChange={(value: OnChangeValue<MSelectChangeData, true>) => setZonesFiltersState(() => value)}
      dateFilters={props.dateFilters}
      onResetDate={props.onResetDate}
    >
      {props.children}
    </MissionControlLayersFiltersView>
  );
};

MissionControlLayersFilters.displayName = 'MissionControlLayersFilters';
MissionControlLayersFilters.defaultProps = {};

export default MissionControlLayersFilters;
